import { useContext, useEffect, useState } from 'react';
import { ModalMobile, ModalDesktop } from '@acko-tech/building-blocks.ui.modal';
import Header from 'widgets/header';
import NewPlan from './newPlan';
import Transactions from './transactions';
import { IOrder } from './types';
import { PageLoader } from 'utils/styles';
import { getImageBasePath } from 'utils/generics';
import ErrorPage from 'widgets/errorScreen';
import { MPosContext } from 'utils/context';
import {
    NewPlanStages,
    policyDetailsInitState,
} from 'components/newPlan/constants';
import * as Styles from './styles';
import { useViewport } from 'utils/responsive';
import LegalDocumentPendingState from './legalDocumentPending';
import SearchInput from '@acko-tech/building-blocks.ui.search-input';

function DashboardV2() {
    const { legalDetailsUploadedStatus, setPlanDetails, setActiveStage } =
        useContext(MPosContext);

    const { isMobile } = useViewport();

    const [pendingOrders, setPendingOrders] = useState<IOrder[]>([]);
    const [completedOrders, setCompletedOrders] = useState<IOrder[]>([]);
    const [pendingOrdersPage, setPendingOrdersPage] = useState(1);
    const [completedOrdersPage, setCompletedOrdersPage] = useState(1);
    const [hasMore, setHasMore] = useState({
        pending: true,
        completed: true,
    });
    const [isPageLoading, setIsPageLoading] = useState(true);
    const [isPageSearchLoading, setIsPageSearchLoading] = useState(false);
    const [errorMesssage, setErrorMessage] = useState('');
    const [searchValue, setSearchValue] = useState('');
    const [isSearchInteracted, setIsSearchInteracted] = useState(false);

    useEffect(() => {
        setPlanDetails(policyDetailsInitState);
        setActiveStage(NewPlanStages.MODEL);
    }, []);

    useEffect(() => {
        import('./apis').then(async (req) => {
            const { status: pendingStatus, data: pendingData } =
                await req.getDealerDashboard(
                    1,
                    'PAYMENT_PENDING, PENDING_VERIFICATION , FAILED'
                );
            const { status: completedStatus, data: completedData } =
                await req.getDealerDashboard(1, 'ISSUED');
            if (
                pendingStatus &&
                pendingData?.orders &&
                completedStatus &&
                completedData?.orders
            ) {
                setPendingOrders(pendingData?.orders || []);
                setCompletedOrders(completedData?.orders || []);
                if (pendingData?.orders?.length > 9) {
                    setPendingOrdersPage(2);
                } else {
                    setHasMore({
                        ...hasMore,
                        pending: pendingData?.orders.length > 9,
                        completed: completedData?.orders.length > 9,
                    });
                }
                if (completedData?.orders?.length > 9) {
                    setCompletedOrdersPage(2);
                } else {
                    setHasMore({
                        ...hasMore,
                        pending: pendingData?.orders.length > 9,
                        completed: completedData?.orders.length > 9,
                    });
                }
                setErrorMessage('');
            } else {
                setErrorMessage('Something went wrong.');
            }
            setIsPageLoading(false);
        });
    }, []);

    const onLoadMore = (selectedTab: 'pending' | 'completed') => {
        import('./apis').then(async (req) => {
            const page =
                selectedTab === 'pending'
                    ? pendingOrdersPage
                    : completedOrdersPage;

            const { status, data } = await req.getDealerDashboard(
                page,
                selectedTab === 'pending'
                    ? 'PAYMENT_PENDING, PENDING_VERIFICATION , FAILED'
                    : 'ISSUED'
            );

            if (status && data?.orders) {
                if (selectedTab === 'pending') {
                    if (data?.orders?.length > 0) {
                        setPendingOrders(pendingOrders.concat(data?.orders));
                        data?.orders?.length > 9 &&
                            setPendingOrdersPage(pendingOrdersPage + 1);
                    }
                    setHasMore({
                        ...hasMore,
                        pending: data?.orders.length > 9,
                    });
                } else if (selectedTab === 'completed') {
                    if (data?.orders?.length > 0) {
                        setCompletedOrders(
                            completedOrders.concat(data?.orders)
                        );
                        data?.orders?.length > 9 &&
                            setCompletedOrdersPage(completedOrdersPage + 1);
                    }
                    setHasMore({
                        ...hasMore,
                        completed: data?.orders.length > 9,
                    });
                }
            }
        });
    };

    function getTransactions() {
        import('./apis').then(async (req) => {
            setIsPageSearchLoading(true);

            const { status: pendingStatus, data: pendingData } =
                await req.getDealerDashboard(
                    1,
                    'PAYMENT_PENDING, PENDING_VERIFICATION , FAILED',
                    searchValue
                );
            const { status: completedStatus, data: completedData } =
                await req.getDealerDashboard(1, 'ISSUED', searchValue);

            if (
                pendingStatus &&
                pendingData?.orders &&
                completedStatus &&
                completedData?.orders
            ) {
                setPendingOrders(pendingData?.orders || []);
                setCompletedOrders(completedData?.orders || []);
                if (pendingData?.orders?.length > 9) {
                    setPendingOrdersPage(2);
                } else {
                    setHasMore({
                        ...hasMore,
                        pending: pendingData?.orders.length > 9,
                        completed: completedData?.orders.length > 9,
                    });
                }
                if (completedData?.orders?.length > 9) {
                    setCompletedOrdersPage(2);
                } else {
                    setHasMore({
                        ...hasMore,
                        pending: pendingData?.orders.length > 9,
                        completed: completedData?.orders.length > 9,
                    });
                }
                setErrorMessage('');
            } else {
                setErrorMessage('Something went wrong.');
            }
            setIsPageSearchLoading(false);
        });
    }

    // TODO : This is causing 2 api call on page load
    useEffect(() => {
        let timeout: any;
        if (
            isSearchInteracted &&
            (searchValue.length === 10 || searchValue.length === 0)
        ) {
            timeout = setTimeout(() => {
                setHasMore({
                    pending: true,
                    completed: true,
                });
                setPendingOrdersPage(1);
                setCompletedOrdersPage(1);
                getTransactions();
                setIsPageSearchLoading(true);
            }, 1000);
        }
        return () => clearTimeout(timeout);
    }, [searchValue, isSearchInteracted]);

    const Modal: any = isMobile ? ModalMobile : ModalDesktop;

    if (isPageLoading) {
        return <PageLoader src={getImageBasePath('/gif/pageLoader.gif')} />;
    }

    if (errorMesssage) {
        return <ErrorPage />;
    }

    return (
        <>
            {isPageSearchLoading && (
                <PageLoader src={getImageBasePath('/gif/pageLoader.gif')} />
            )}
            <Styles.Wrapper>
                <Header />
                <Styles.Content>
                    <Styles.Section $index={0}>
                        <NewPlan />
                    </Styles.Section>
                    <Styles.Section $index={1}>
                        <Styles.TransactionSearchFlex>
                            <div>
                                <SearchInput
                                    className={''}
                                    placeholder={'Search by mobile number'}
                                    handleChange={(value) => {
                                        setSearchValue(value);
                                        setIsSearchInteracted(true);
                                    }}
                                />
                            </div>
                        </Styles.TransactionSearchFlex>
                    </Styles.Section>
                    <Styles.Section $index={2}>
                        <Transactions
                            pendingOrders={pendingOrders}
                            completedOrders={completedOrders}
                            hasMorePending={hasMore.pending}
                            hasMoreCompleted={hasMore.completed}
                            onLoadMore={onLoadMore}
                        />
                    </Styles.Section>
                </Styles.Content>
            </Styles.Wrapper>
            {legalDetailsUploadedStatus && (
                <Modal
                    show={true}
                    urlhash={''}
                    onHide={() => {}}
                    padding={'20px'}
                    wrapperStyles={`border-radius: 16px 16px 0px 0px;`}
                    showOverlayBackground={true}
                    hideCloseButton={true}
                >
                    <LegalDocumentPendingState />
                </Modal>
            )}
        </>
    );
}

export default DashboardV2;
